import {
	WrenchScrewdriverIcon,
	GlobeAltIcon,
	ChatBubbleLeftRightIcon,
	Cog6ToothIcon,
	UsersIcon,
	EnvelopeIcon,
	DocumentTextIcon,
	ServerStackIcon,
	CloudIcon,
	ChartBarIcon,
	ShieldCheckIcon,
	ShieldExclamationIcon,
	LifebuoyIcon,
	RocketLaunchIcon
} from "@heroicons/react/24/outline";
import { AppRoute } from "./types";

const iconClass = "w-5 h-5 lg:w-6 lg:h-6";

const createRoute = ({
	id,
	url,
	component,
	isPrivate = true,
	isNavItem = false,
	name = "",
	icon = null,
	showMenu = true,
	isChildNavItem = false,
	children = [],
	exact = true,
	requiredRoles = [],
	toolSlug
}: AppRoute) => {
	return {
		id,
		name,
		url,
		component,
		icon,
		showMenu,
		isPrivate,
		isNavItem,
		isChildNavItem,
		children,
		exact,
		requiredRoles,
		toolSlug
	};
};

const AppRoutes = {
	LOGIN: createRoute({
		id: "login",
		url: "/login",
		component: "login/containers/LoginContainer",
		isPrivate: false,
		isNavItem: false,
		name: "Login",
		icon: null,
		showMenu: false
	}),
	NOT_AUTHORIZED: createRoute({
		id: "403",
		url: "/403",
		component: "errors/containers/NotAuthorizedContainer",
		isPrivate: false,
		isNavItem: false,
		name: "",
		icon: null,
		showMenu: false
	}),
	NOT_FOUND: createRoute({
		id: "404",
		url: "/404",
		component: "errors/containers/NotFoundContainer",
		isPrivate: false,
		isNavItem: false,
		name: "",
		icon: null,
		showMenu: false
	}),
	AI_ASSISTANT: createRoute({
		id: "ai-assistant",
		url: "/",
		component: "ai/containers/AiContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Ask Scout",
		icon: <ChatBubbleLeftRightIcon className={iconClass} />,
		showMenu: true
	}),
	APP_BUILDER: createRoute({
		id: "app-builder",
		url: "/app-builder",
		component: "app-builder/containers/AppBuilderContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Sidekick Builder",
		icon: <WrenchScrewdriverIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "app-builder"
	}),
	DNS_MANAGER: createRoute({
		id: "dns-manager",
		url: "/dns",
		component: "dns/containers/DnsContainer",
		isPrivate: true,
		isNavItem: true,
		name: "DNS Manager",
		icon: <GlobeAltIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "dns-manager"
	}),
	EMAIL_SEARCH: createRoute({
		id: "email-search",
		url: "/email",
		component: "email/containers/EmailContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Email Search",
		icon: <EnvelopeIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "email-search"
	}),
	AWS_MONITOR: createRoute({
		id: "aws-monitor",
		url: "/aws",
		component: "aws/containers/AwsContainer",
		isPrivate: true,
		isNavItem: true,
		name: "AWS Monitor",
		icon: <ServerStackIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "aws-monitor"
	}),
	CLOUDFRONT_MANAGER: createRoute({
		id: "cloudfront-manager",
		url: "/cloudfront",
		component: "cloudfront/containers/CloudFrontContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Cache Buster",
		icon: <CloudIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "cloudfront-manager"
	}),
	IP_REQUESTS: createRoute({
		id: "ip-requests",
		url: "/ip-requests",
		component: "ip-requests/containers/IpRequestsContainer",
		isPrivate: true,
		isNavItem: true,
		name: "IP Requests",
		icon: <ShieldCheckIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "ip-requests"
	}),
	SALESFORCE_CASE: createRoute({
		id: "salesforce-case",
		url: "/salesforce-case",
		component: "salesforce-case/containers/SalesforceCaseContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Case Triage",
		icon: <LifebuoyIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "salesforce-case"
	}),
	IMPLEMENTATION_TIMELINE: createRoute({
		id: "implementation-timeline",
		url: "/implementation-timeline",
		component: "implementation-timeline/containers/ImplementationTimelineContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Implementation Timeline",
		icon: <RocketLaunchIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "implementation-timeline"
	}),
	BOT_WATCH: createRoute({
		id: "bot-watch",
		url: "/bot-watch",
		component: "bot-watch/containers/BotWatchContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Bot Watch",
		icon: <ShieldExclamationIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "bot-watch"
	}),
	SOURCE_REPORTS: createRoute({
		id: "source-reports",
		url: "/source-reports",
		component: "source-reports/containers/SourceReportsContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Source Reports",
		icon: <ChartBarIcon className={iconClass} />,
		showMenu: true,
		toolSlug: "source-reports"
	}),
	TECH_DOCS: createRoute({
		id: "tech-docs",
		url: "/docs",
		component: "docs/containers/DocsContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Documentation",
		icon: <DocumentTextIcon className={iconClass} />,
		showMenu: true
	}),
	ADMIN: createRoute({
		id: "admin",
		url: "/admin",
		component: "admin/containers/AdminContainer",
		isPrivate: true,
		isNavItem: true,
		name: "User Management",
		icon: <UsersIcon className={iconClass} />,
		showMenu: true,
		requiredRoles: ["admin", "department-lead"]
	}),
	SETTINGS: createRoute({
		id: "settings",
		url: "/settings",
		component: "settings/containers/SettingsContainer",
		isPrivate: true,
		isNavItem: true,
		name: "Settings",
		icon: <Cog6ToothIcon className={iconClass} />,
		showMenu: true
	})
};

export { AppRoutes };
